import {FC, useEffect, useMemo, useState} from 'react'
import './cardInfoGuide.css'

export type CardInfoItem = {
  label: string
  description: string
}

export type CardInfoContent = {
  title: string
  subtitle?: string
  items: CardInfoItem[]
}

type CardInfoGuideProps = {
  content: CardInfoContent
  interaction?: 'click' | 'hover' | 'both'
}

const CardInfoGuide: FC<CardInfoGuideProps> = ({content, interaction = 'click'}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const triggerByHover = useMemo(
    () => interaction === 'hover' || interaction === 'both',
    [interaction]
  )
  const triggerByClick = useMemo(
    () => interaction === 'click' || interaction === 'both',
    [interaction]
  )

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false)
      }
    }

    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [isModalOpen])

  return (
    <>
      <div
        className='card-info-guide'
        onMouseEnter={() => triggerByHover && setIsPopoverOpen(true)}
        onMouseLeave={() => triggerByHover && setIsPopoverOpen(false)}
      >
        <button
          type='button'
          className='btn btn-sm btn-icon card-info-guide__trigger'
          aria-label='Ajuda deste card'
          onClick={() => triggerByClick && setIsModalOpen(true)}
        >
          <i className='bi bi-info-circle'></i>
        </button>

        {isPopoverOpen && (
          <div className='card-info-guide__popover'>
            <div className='card-info-guide__popover-title'>{content.title}</div>
            {content.subtitle && (
              <div className='card-info-guide__popover-subtitle'>{content.subtitle}</div>
            )}
            <ul className='card-info-guide__popover-list'>
              {content.items.slice(0, 3).map((item) => (
                <li key={item.label}>
                  <strong>{item.label}:</strong> {item.description}
                </li>
              ))}
            </ul>
            {content.items.length > 3 && (
              <div className='card-info-guide__popover-footer'>Clique para ver o guia completo</div>
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className='card-info-guide__modal-backdrop' onClick={() => setIsModalOpen(false)}>
          <div className='card-info-guide__modal' onClick={(e) => e.stopPropagation()}>
            <div className='card-info-guide__modal-header'>
              <div>
                <h4 className='card-info-guide__modal-title mb-0'>{content.title}</h4>
                {content.subtitle && (
                  <div className='card-info-guide__modal-subtitle mt-1'>{content.subtitle}</div>
                )}
              </div>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light card-info-guide__close'
                onClick={() => setIsModalOpen(false)}
                aria-label='Fechar guia'
              >
                <i className='bi bi-x-lg'></i>
              </button>
            </div>

            <div className='card-info-guide__modal-body'>
              <ul className='card-info-guide__modal-list'>
                {content.items.map((item) => (
                  <li key={item.label} className='card-info-guide__modal-item'>
                    <div className='card-info-guide__item-label'>{item.label}</div>
                    <div className='card-info-guide__item-description'>{item.description}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className='card-info-guide__modal-footer'>
              <button
                type='button'
                className='btn btn-light-primary fw-semibold'
                onClick={() => setIsModalOpen(false)}
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export {CardInfoGuide}
